/**
 * Notification Service
 *
 * API client for notification endpoints
 */

import apiClient from './api';
import {
  Notification,
  NotificationPreferences,
  UpdateNotificationPreferencesRequest,
  PushSubscription,
  CreatePushSubscriptionRequest,
  NotificationsResponse,
  UnreadCountResponse,
  NotificationPreferencesResponse,
  VapidKeyResponse,
  NotificationScope,
} from '@/types/notification';

/** Always explicit: the server's inference from the caller must never decide what the client meant. */
const apiScope = (scope: NotificationScope): 'brand' | 'portfolio' => (scope === 'portfolio' ? 'portfolio' : 'brand');

export const notificationService = {
  // ===========================
  // Notifications
  // ===========================

  /**
   * Get user notifications with pagination
   */
  getNotifications: async (
    page = 1,
    limit = 20,
    unreadOnly = false,
    scope: NotificationScope = 'team'
  ): Promise<NotificationsResponse['data']> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (unreadOnly) {
      params.append('unreadOnly', 'true');
    }
    params.append('scope', apiScope(scope));

    const response = await apiClient.get<NotificationsResponse>(
      `/api/notifications?${params.toString()}`
    );
    return response.data.data;
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (scope: NotificationScope = 'team'): Promise<number> => {
    const response = await apiClient.get<UnreadCountResponse>(
      '/api/notifications/unread-count',
      { params: { scope: apiScope(scope) } }
    );
    return response.data.data.count;
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (notificationId: string): Promise<Notification> => {
    const response = await apiClient.post<{ success: boolean; data: Notification }>(
      `/api/notifications/${notificationId}/read`
    );
    return response.data.data;
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (scope: NotificationScope = 'team'): Promise<void> => {
    await apiClient.post(
      '/api/notifications/mark-all-read',
      undefined,
      { params: { scope: apiScope(scope) } }
    );
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (notificationId: string): Promise<void> => {
    await apiClient.delete(`/api/notifications/${notificationId}`);
  },

  // ===========================
  // Notification Preferences
  // ===========================

  /**
   * Get notification preferences
   */
  getPreferences: async (): Promise<NotificationPreferences> => {
    const response = await apiClient.get<NotificationPreferencesResponse>(
      '/api/notifications/preferences'
    );
    return response.data.data;
  },

  /**
   * Update notification preferences
   */
  updatePreferences: async (
    preferences: UpdateNotificationPreferencesRequest
  ): Promise<NotificationPreferences> => {
    const response = await apiClient.put<NotificationPreferencesResponse>(
      '/api/notifications/preferences',
      preferences
    );
    return response.data.data;
  },

  // ===========================
  // Push Notifications
  // ===========================

  /**
   * Get VAPID public key for push subscriptions
   */
  getVapidPublicKey: async (): Promise<string> => {
    const response = await apiClient.get<VapidKeyResponse>(
      '/api/notifications/push-subscriptions/vapid-public-key'
    );
    return response.data.data.publicKey;
  },

  /**
   * Subscribe to push notifications
   */
  subscribeToPush: async (
    subscription: CreatePushSubscriptionRequest
  ): Promise<PushSubscription> => {
    const response = await apiClient.post<{ success: boolean; data: PushSubscription }>(
      '/api/notifications/push-subscriptions',
      subscription
    );
    return response.data.data;
  },

  /**
   * Unsubscribe from push notifications
   */
  unsubscribeFromPush: async (subscriptionId: string): Promise<void> => {
    await apiClient.delete(`/api/notifications/push-subscriptions/${subscriptionId}`);
  },
};

export default notificationService;
